import { useEffect, useRef, useState } from 'react'
import './LoadingScreenNew.css'

const MAX_WAIT_MS = 8000

function LoadingScreenNew({ onFinished }) {
  const [fade, setFade] = useState(false)
  const [videoVisible, setVideoVisible] = useState(false)
  const videoRef = useRef(null)
  const doneRef = useRef(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // React's `muted` JSX prop sets the HTML attribute, not the DOM property.
    // Mobile autoplay policy checks the DOM property — must set explicitly.
    video.muted = true

    if (typeof video.disablePictureInPicture !== 'undefined') {
      video.disablePictureInPicture = true
    }

    const finish = () => {
      if (doneRef.current) return
      doneRef.current = true
      setFade(true)
      setTimeout(() => { if (onFinished) onFinished() }, 900)
    }

    const maxTimer = setTimeout(finish, MAX_WAIT_MS)
    const showVideo = () => setVideoVisible(true)

    video.addEventListener('ended', () => { clearTimeout(maxTimer); finish() }, { once: true })
    video.addEventListener('error', () => { clearTimeout(maxTimer); finish() }, { once: true })
    video.addEventListener('canplay', showVideo, { once: true })
    video.addEventListener('playing', showVideo, { once: true })

    // Handle video already ready from cache before listener attached
    if (video.readyState >= 3) setVideoVisible(true)

    // Track document listeners for cleanup if autoplay is blocked
    let retryCleanup = null

    const playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked — retry on first user interaction
        const retry = () => {
          retryCleanup = null
          video.play().catch(() => { clearTimeout(maxTimer); finish() })
        }
        document.addEventListener('touchstart', retry, { once: true, passive: true })
        document.addEventListener('click', retry, { once: true })
        retryCleanup = () => {
          document.removeEventListener('touchstart', retry)
          document.removeEventListener('click', retry)
        }
      })
    }

    return () => {
      clearTimeout(maxTimer)
      retryCleanup?.()
    }
  }, [onFinished])

  return (
    <div className={`loading-screen-new${fade ? ' loading-screen-new--fade-out' : ''}`}>
      <video
        ref={videoRef}
        className={`loading-screen-new__video${videoVisible ? ' loading-screen-new__video--visible' : ''}`}
        preload="auto"
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noplaybackrate"
      >
        <source src="/video/loading.mp4" type="video/mp4" />
      </video>
    </div>
  )
}

export default LoadingScreenNew
